import { Component, Input, ElementRef, Renderer2, OnInit, OnDestroy, ViewChild, AfterViewInit, QueryList, ViewChildren } from '@angular/core';

// Fallback announcement for usages that don't pass an explicit [text], so screen readers
// still hear something even when there's no visible caption.
const DEFAULT_ANNOUNCEMENT = 'Page loading, please wait';
// aria-live regions are only re-announced by screen readers when their content changes,
// so we periodically toggle an invisible marker to force re-announcement while the loader is up.
const REANNOUNCE_INTERVAL_MS = 10000;

@Component({
    selector: 'static-loader',
    templateUrl: './static-loader.component.html',
    styleUrls: ['./static-loader.component.scss'],
    standalone: false
})
export class StaticLoaderComponent implements OnInit, AfterViewInit, OnDestroy {
    public _diameter: number;
    @ViewChild('loader', { read: ElementRef, static: true })
    loaderElement: ElementRef;
    @ViewChildren('textElement', { read: ElementRef })
    textElement: QueryList<ElementRef>;
    @Input()
    color: string = 'black';
    @Input()
    text: string;
    // Set to false when a parent that outlives this component's mount/unmount cycle (e.g. one
    // that needs to announce completion after this component is destroyed) owns the aria-live
    // announcement itself, so the two don't end up as competing live regions.
    @Input()
    announce: boolean = true;
    @Input()
    set diameter(val: number) {
        this._diameter = val;

        if (!this.loaderElement)
            return;

        let div = this.loaderElement.nativeElement;
        this._renderer2.setStyle(div, 'width', `${this._diameter}px`);
        this._renderer2.setStyle(div, 'height', `${this._diameter}px`);
    }

    get diameter() {
        return this._diameter
    }

    // Toggled on an interval to change the aria-live region's content so screen readers
    // periodically re-announce it, without altering what's visually or audibly conveyed.
    private _announceTick: boolean = false;
    private _announceIntervalId: ReturnType<typeof setInterval> | undefined;

    get announceText(): string {
        return (this.text || DEFAULT_ANNOUNCEMENT) + (this._announceTick ? '⁠' : '');
    }

    constructor(
        public _renderer2: Renderer2
    ) { }

    ngOnInit() {
        if (!this.diameter)
            this.diameter = 80;

        this._announceIntervalId = setInterval(() => {
            this._announceTick = !this._announceTick;
        }, REANNOUNCE_INTERVAL_MS);
    }

    ngAfterViewInit() {
        this.diameter = this._diameter;
    }

    ngOnDestroy() {
        clearInterval(this._announceIntervalId);
    }
}
